import React, { FC } from "react";
import { Box, IconButton, Typography, Divider, Tooltip } from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { DirectBillingPayerGroupDto } from "../../../../../../api-client";
import { ValidationMap } from "../types/interface";
import GkvBillTable from "./GkvBillTable";
import { NotesField } from "../../NewBill/components/sub-components/Notes";
import { CompactCard, CompactCardContent } from "../styles/primitives";
import { colors, spacing, radius } from "../styles/tokens";

export interface GkvBillCardProps {
  bill: DirectBillingPayerGroupDto;
  validationMap: ValidationMap;
  account: string;
  userName: string;
  selectedRxId: string | null;
  selectedRxIds: Set<string>;
  onRowClick: (rx: any) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLButtonElement>, rx: any) => void;
  onRemoveAll: (payerIK: string) => void;
  onToggleRxSelection: (rxId: string) => void;
  notes: string;
  onNotesChange: (value: string) => void;
}

/**
 * A single payer group card.
 *
 * Shows payer name, receiving center info, total price,
 * a remove-all button, and the Rx table underneath.
 */
const GkvBillCard: FC<GkvBillCardProps> = ({
  bill,
  validationMap,
  account,
  userName,
  selectedRxId,
  selectedRxIds,
  onRowClick,
  onMenuOpen,
  onRemoveAll,
  onToggleRxSelection,
  notes,
  onNotesChange,
}) => {
  const { payerIK, payerName, paperReceivingCenter, totalPrice, rxs } = bill;
  const totalPriceFormatted = Number(totalPrice).toFixed(2).replace(".", ",");
  const todayString = new Date().toLocaleDateString("de-DE");

  return (
    <CompactCard>
      {/* Card header with payer name and actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          background: `${colors.payerName}`,
          borderBottom: `2px solid ${colors.borderLight}`,
          borderRadius: `${radius.lg} ${radius.lg} 0 0`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Tooltip title="Ganz entfernen" arrow placement="top">
            <IconButton
              size="small"
              onClick={() => onRemoveAll(payerIK)}
              sx={{
                color: colors.lightWarning,
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": {
                  color: "#ff9800",
                  backgroundColor: "rgba(255, 152, 0, 0.08)",
                  boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
                },
              }}
            >
              <RemoveCircleOutlineIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <BusinessIcon sx={{ color: colors.accent, fontSize: 28 }} />
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              color={colors.textPrimary}
              sx={{ lineHeight: 1.2 }}
            >
              {payerName}
            </Typography>
            <Typography variant="caption" color={colors.textPrimary} sx={{ fontSize: "0.85rem" }}>
              IK: {payerIK}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              backgroundColor: colors.headerBg,
              color: "white",
              px: 2,
              py: 1,
              borderRadius: radius.md,
              textAlign: "right",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: "0.7rem", opacity: 0.9, display: "block", lineHeight: 1 }}
            >
              Gesamtpreis
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {totalPriceFormatted} €
            </Typography>
          </Box>
        </Box>
      </Box>

      <CompactCardContent>
        {/* Metadata grid with icons */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: paperReceivingCenter ? "2fr 1fr 1fr 1fr" : "1fr 1fr 1fr",
            gap: 1.5,
            mb: 2,
          }}
        >
          {paperReceivingCenter && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                backgroundColor: colors.infoBg,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: radius.md,
              }}
            >
              <ReceiptIcon sx={{ color: colors.info, fontSize: 20, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color={colors.textMuted}
                  sx={{ fontSize: "0.7rem", display: "block", lineHeight: 1.2 }}
                >
                  Papierzentrum
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color={colors.textPrimary}
                  sx={{
                    fontSize: "0.9rem",
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                  }}
                >
                  {paperReceivingCenter.name}
                </Typography>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              backgroundColor: colors.infoBg,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: radius.md,
            }}
          >
            <AccountBalanceIcon sx={{ color: colors.info, fontSize: 20, flexShrink: 0 }} />
            <Box>
              <Typography
                variant="caption"
                color={colors.textMuted}
                sx={{ fontSize: "0.7rem", display: "block", lineHeight: 1.2 }}
              >
                Mandant
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                color={colors.textPrimary}
                sx={{ fontSize: "0.9rem", lineHeight: 1.3 }}
              >
                {account}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              backgroundColor: colors.infoBg,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: radius.md,
            }}
          >
            <CalendarTodayIcon sx={{ color: colors.info, fontSize: 20, flexShrink: 0 }} />
            <Box>
              <Typography
                variant="caption"
                color={colors.textMuted}
                sx={{ fontSize: "0.7rem", display: "block", lineHeight: 1.2 }}
              >
                Startdatum
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                color={colors.textPrimary}
                sx={{ fontSize: "0.9rem", lineHeight: 1.3 }}
              >
                {todayString}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.5,
              backgroundColor: colors.infoBg,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: radius.md,
            }}
          >
            <PersonIcon sx={{ color: colors.info, fontSize: 20, flexShrink: 0 }} />
            <Box>
              <Typography
                variant="caption"
                color={colors.textMuted}
                sx={{ fontSize: "0.7rem", display: "block", lineHeight: 1.2 }}
              >
                Erstellt von
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                color={colors.textPrimary}
                sx={{ fontSize: "0.9rem", lineHeight: 1.3 }}
              >
                {userName}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Rx table */}
        <GkvBillTable
          rxs={rxs}
          validationMap={validationMap}
          selectedRxId={selectedRxId}
          selectedRxIds={selectedRxIds}
          onRowClick={onRowClick}
          onMenuOpen={onMenuOpen}
          onToggleRxSelection={onToggleRxSelection}
        />
        <NotesField value={notes} onChange={onNotesChange} />
      </CompactCardContent>
    </CompactCard>
  );
};

export default React.memo(GkvBillCard);
